import _ from 'lodash';
import axios from 'axios/axios-base';
import { action } from 'typesafe-actions';
import { ActionCreator, AnyAction } from 'redux';
import { AnalyticsTabItem, MsAnalyticsTabType } from 'store/analytics/ui/analyticsUiTypes';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ApplicationState } from 'store/rootReducer';
import { AnalyticsMetaDataDto, MockServiceAnalytics, TRANSACTIONAL } from 'types/rest/mockServiceTypes';
import { createSlug } from 'utils/urlUtils';
import {
  ADD_ANALYTICS_TAB,
  CHANGE_MS_ANALYTICS_CONTENT_TAB,
  CLEAR_ANALYTICS_TABS,
  CLOSE_ANALYTICS_TAB,
  SELECT_ANALYTICS_TAB,
  SELECT_INSPECTION_VIEW_REQUEST_FOR_TAB,
  SELECT_WEBHOOK_VIEW_REQUEST_FOR_TAB,
  SET_ACTIVE_RUNNER_TAB,
  SET_IS_MOCK_SERVICE_DATA_INITIALISED,
  SET_IS_MOCK_SERVICES_LOADING,
  UPDATE_ANALYTICS_TAB_META,
} from 'store/analytics/ui/analyticsUiActionTypes';
import { initialMockServiceAnalyticsState } from 'store/analytics/ui/analyticsUiReducer';
import {
  createActiveDataContext,
  selectDataContextThunk,
  updateCurrentDataContext,
} from 'store/analytics/data/analyticsDataActions';
import { DimensionCriteria, HttpRequest, InspectionViewRequest, TxiViewRequest } from 'types/rest/analyticsTypes';
import MixpanelService from 'utils/mixpanelService';
import { OPEN_ANALYTICS_SUBVIEW } from 'utils/mixpanelEvents';
import { DateRange } from 'views/Analytics/AnalyticsMockService/Reporting/Pickers/BzmDateRangePicker/BzmDateRangePicker';
import { CraneLocation } from 'types/rest/locationTypes';
import { removeMillisFromISOString } from 'utils/dateUtil';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { hideBackdrop } from 'store/ui/backdrop/backdropActions';
import { RunnerTypes } from '../../../components/ui/ButtonGroups/RunnersButtonGroup';

export type MockServiceSearchCriteria = {
  from: Date;
  to: Date;
  pageIndex: number;
  smName?: string;
  location?: CraneLocation | null;
  includeDeleted: boolean;
};

/**,
 * Closes tab in analytics (UI)
 * @param tabId
 */
export const closeAnalyticsTab = (tabId: string | number) => action(CLOSE_ANALYTICS_TAB, tabId);

export const updateAnalyticsTabMeta = (tabId: string | number, meta: any) =>
  action(UPDATE_ANALYTICS_TAB_META, {
    id: tabId,
    meta: meta,
  });

/**
 * Close tab action. It closes the tab selects the closest one. After this action
 * it reloads data context for this newly selected tab.
 * @param tabId
 */
export const closeAnalyticsTabThunk = (tabId: string | number): ThunkAction<any, ApplicationState, null, any> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    // Closes current tab and selects new one
    await dispatch(closeAnalyticsTab(tabId));

    // Select correct data context
    const activeTabId = getState().analytics.ui.activeTab;
    if (!_.isNil(activeTabId)) {
      dispatch(selectDataContextThunk(activeTabId));
    } else {
      //No tab selected, clearing current data context
      dispatch(updateCurrentDataContext(null));
    }
  };
};

/**
 * Add new tab item in the analytics.
 * @param tabItem
 */
export const addAnalyticsTab = (tabItem: AnalyticsTabItem) => action(ADD_ANALYTICS_TAB, tabItem);

/**
 * Add new tab item in the analytics.
 * @param tabItem
 */
export const selectTab = (tabId: string | number) => action(SELECT_ANALYTICS_TAB, tabId);

/**
 * When we switch between tabs tabs we need to change UI context and Data context.
 * @param tabId
 */
export const selectTabThunk: ActionCreator<any> = (
  tabId: string | number,
): ThunkAction<any, ApplicationState, null, any> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    // Check if the tab is not already selected, do not select it again
    const selectedTabId = getState().analytics.ui.activeTab;
    if (String(selectedTabId) === String(tabId)) {
      return;
    }

    dispatch(selectTab(tabId));
    dispatch(selectDataContextThunk(tabId));
  };
};

/**
 * Clear all opened analytics tabs.
 */
export const clearAnalyticsTabs = () => action(CLEAR_ANALYTICS_TABS);

/**
 * Change current tab for current ms analytics view.
 * @param tab
 */
export const selectMsAnalyticsContentTab = (tab: MsAnalyticsTabType) => {
  MixpanelService.track(OPEN_ANALYTICS_SUBVIEW(tab));
  return action(CHANGE_MS_ANALYTICS_CONTENT_TAB, tab);
};

export const setActiveRunnerTab = (runner: RunnerTypes) => action(SET_ACTIVE_RUNNER_TAB, runner);

export const setIsMockServicesLoading = (isLoading: boolean) => action(SET_IS_MOCK_SERVICES_LOADING, isLoading);

export const setIsMockservicesMetadataInitialised = (isInitalised: boolean) =>
  action(SET_IS_MOCK_SERVICE_DATA_INITIALISED, isInitalised);

/**
 * Thunk action for open virtual service from virtual service screen. In order to open analytics tab, we need to
 * load virtual service metadata. */
export const openAnalyticsTabItemThunk: ActionCreator<any> = (
  mockServiceId: number,
): ThunkAction<any, ApplicationState, null, any> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    try {
      const metaUrl = `/workspaces/{workspaceId}/analytics/metadata/${mockServiceId}`;
      const mockServiceMeta = (await axios.get(metaUrl)).data.result;

      if (_.isNil(mockServiceMeta)) {
        throw new Error('Virtual Service meta not available');
      }

      const analyticsTabItem: MockServiceAnalytics = {
        id: mockServiceMeta.serviceMockId,
        name: mockServiceMeta.name,
        meta: mockServiceMeta,
        type: mockServiceMeta.type,
      } as MockServiceAnalytics;

      await dispatch(createAnalyticsTabItemThunk(analyticsTabItem));
    } catch (error) {
      const errorMsg: string = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, 'Cannot Open Analytics tab', errorMsg));
    } finally {
      await dispatch(hideBackdrop());
    }
  };
};

/**
 * Creates tab item in the analytics scrollbar. If tab already exists, it just selects it.
 * @param newItem
 * @param dateRange preselected date range used as initial value in ui state
 */
export const createAnalyticsTabItemThunk: ActionCreator<any> = (
  newItem: MockServiceAnalytics,
  dateRange?: DateRange,
): ThunkAction<any, ApplicationState, null, any> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    const tabs = getState().analytics.ui.tabs;

    // If this report is already open, just focus on it, else open it, comparison has to compare only strings
    if (tabs.findIndex((item: AnalyticsTabItem) => String(item.id) === String(newItem.id)) !== -1) {
      await dispatch(selectTabThunk(String(newItem.id)));
    } else {
      // Prepare new UI state
      const tabItem: AnalyticsTabItem = {
        id: newItem.id.toString(),
        name: newItem.name,
        slug: createSlug(newItem.name, newItem.id),
        type: 'MOCK_SERVICE',
        subType: newItem.meta ? newItem.meta.type : null,
        meta: newItem,
        uiState: prepareInitialMockServiceAnalyticsState(dateRange),
      };
      await dispatch(addAnalyticsTab(tabItem));

      // Prepare data context (async)
      await dispatch(createActiveDataContext(newItem.id));
    }
  };
};

/**
 * Refreshes metadata of currently opened tab.
 * @param msId
 */
export const refreshMsMetadataThunk: ActionCreator<any> = (
  msId: string | number,
): ThunkAction<any, ApplicationState, null, any> => {
  return async (dispatch: ThunkDispatch<any, any, any>) => {
    try {
      const metaUrl = `/workspaces/{workspaceId}/analytics/metadata/${msId}`;
      const mockServiceMeta = (await axios.get(metaUrl)).data.result;
      dispatch(updateAnalyticsTabMeta(msId, mockServiceMeta));
    } catch (error) {
      const errorMsg: string = _.get(error, 'response.data.error', error.toString());
      console.log('Cannot update tab, reason: ', errorMsg);
    }
  };
};

/**
 * Prepares ui state for newly opened tab item. There might be some overrides of the defaults.
 * @param dateRange
 */
const prepareInitialMockServiceAnalyticsState = (dateRange?: DateRange) => {
  //Override default date range if there is one specified
  if (!_.isNil(dateRange)) {
    const initialMsState = _.cloneDeep(initialMockServiceAnalyticsState);
    initialMsState.reportingView.dateRange = dateRange;
    initialMsState.reportingView.fixedTimeRange = null;
    return initialMsState;
  }

  return initialMockServiceAnalyticsState;
};

const ANALYTICS_MOCK_SERVICES_PAGE_SIZE = 20;

/**
 * Gets Running virtual services for Analytics dropdown by search criteria.
 */
export const getMockServicesBySearchCriteria = async (criteria: MockServiceSearchCriteria) => {
  const dimensionCriteria: DimensionCriteria = {
    from: removeMillisFromISOString(criteria.from.toISOString()),
    to: removeMillisFromISOString(criteria.to.toISOString()),
    harborId: criteria.location ? criteria.location.harborId : undefined,
    shipId: criteria.location ? criteria.location.shipId : undefined,
    includeDeleted: criteria.includeDeleted,
    smName: criteria.smName,
  };

  const urlParams = {
    limit: ANALYTICS_MOCK_SERVICES_PAGE_SIZE,
    skip: criteria.pageIndex * ANALYTICS_MOCK_SERVICES_PAGE_SIZE,
  };

  const metaDataUrl = `/workspaces/{workspaceId}/analytics/metadata`;
  return (await axios.post(metaDataUrl, dimensionCriteria, { params: urlParams })).data;
};

export const selectInspectionViewRequestForTab = (
  tabId: string | number,
  request: InspectionViewRequest | TxiViewRequest | null,
) =>
  action(SELECT_INSPECTION_VIEW_REQUEST_FOR_TAB, {
    tabId: tabId,
    request: request,
  });

export const selectWebhookViewRequestForTab = (tabId: string | number, webhook: HttpRequest | null) =>
  action(SELECT_WEBHOOK_VIEW_REQUEST_FOR_TAB, {
    tabId: tabId,
    request: webhook,
  });
